import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import NativeAppCapabilities from '~/constants/common/NativeAppCapabilities';
import StaticContext from '~/context/common/StaticContext';
import { getAppCapabilitiesCookieName } from '~/native/utils';
import BrowserCookies from '~/utilities/BrowserCookies';
import { useSetFeatureBranch, useSetNativeAppCapabilities } from './hooks';
import NativeAppCapabilitiesSection from './NativeAppCapabilitiesSection';
import FormField from '../FormField';
import { Button, ErrorMessage, Input } from '../PassengerTools.styles';

export default function BranchName() {
  const { clientId, kubernetesAppName } = useContext(StaticContext);
  const [errorMessage, setErrorMessage] = useState('');
  const setFeatureBranch = useSetFeatureBranch(
    kubernetesAppName,
    setErrorMessage
  );
  const setNativeAppCapabilities = useSetNativeAppCapabilities(clientId);

  const handleSaveBranch = useCallback(
    (
      newBranchName: string,
      nativeAppCapabilities: string[],
      setForAllApps: boolean
    ) => {
      const featureBranchSet = setFeatureBranch(newBranchName, setForAllApps);
      const nativeAppCapabilitiesSet = setNativeAppCapabilities(
        nativeAppCapabilities
      );

      if (featureBranchSet || nativeAppCapabilitiesSet) {
        window.location.reload();
      } else {
        setErrorMessage('Branch name or app capabilities must be set.');
      }
    },
    [setFeatureBranch, setNativeAppCapabilities]
  );

  const nativeAppCapabilityOptions = useMemo(
    () => Object.values(NativeAppCapabilities[clientId] ?? {}),
    [clientId]
  );

  const currentNativeAppCapabilities = BrowserCookies.get(
    getAppCapabilitiesCookieName(clientId)
  );

  const onSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const branchName = String(data.get('branch-name') ?? '');
    const nativeAppCapabilities = Array.from(
      data.getAll('native-app-capability')
    ).map(v => String(v));
    const setForAllApps = data.get('set-for-all-apps') === 'on';

    handleSaveBranch(branchName, nativeAppCapabilities, setForAllApps);
  };

  useEffect(() => {
    document.getElementById('branch-name')?.focus();
  }, []);

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-lg">
      <FormField label="Branch Name" htmlFor="branch-name">
        <Input
          autoCorrect="off"
          type="text"
          id="branch-name"
          name="branch-name"
          placeholder="reo-12345"
        />
      </FormField>

      <label className="flex gap-8 text-white">
        <input
          type="checkbox"
          id="set-for-all-apps"
          name="set-for-all-apps"
          data-analytics-id={null}
          data-testid="set-for-all-apps"
        />
        <span>Set for all apps</span>
      </label>

      <NativeAppCapabilitiesSection
        nativeAppCapabilityOptions={nativeAppCapabilityOptions}
        currentNativeAppCapabilities={currentNativeAppCapabilities}
      />

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <div className="flex justify-end pt-md">
        <Button
          type="submit"
          data-analytics-id={null}
          data-testid="save-branch"
        >
          Save
        </Button>
      </div>
    </form>
  );
}
